import { Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Chip, Container, FormControl, FormControlLabel, Grid, IconButton, InputLabel, List, ListItem, MenuItem, OutlinedInput, Paper, Select, SelectChangeEvent, Snackbar, Stack, Switch, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Title from '../components/Title';
import { Storefront } from '../models/StorefrontResponse';
import { useProductsAll } from "../hooks/useProductsAll";
import { Product } from '../models/Products';
import { useBlueprint } from '../hooks/useBlueprint';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { useDataSources } from '../hooks/useDataSources';
import { useDataSource } from '../hooks/useDataSource';
import QueryFieldForm from '../components/QueryFieldForm';
import { QueryItem } from '../models/DataSource';
import { genUniqueId } from '../utils/GenerateUniqueID';
import { Blueprint, BlueprintResponse, QueryBlueprint, QueryFilter } from '../models/Blueprint';
import useAuth from "../hooks/useAuth";
import { useBlueprintCounts } from '../hooks/useBlueprintCounts';
import { Theme, useTheme } from '@mui/material/styles';
import { httpPost, APIEndpoint, EndpointType } from "../utils/apiService";
import CloseIcon from "@mui/icons-material/Close";
import { useBlueprints } from '../hooks/useBlueprints';
import { AutocompleteUser, useUsers } from '../hooks/useUsers';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface Props {
    storefront: Storefront
}

const ListBuilder = (props: Props) => {
    const [ blueprintID, setBlueprintID ] = useState("");
    const [ openBlueprintID, setOpenBlueprintID ] = useState("");
    const [ dataSourceID, setDataSourceID ] = useState("");
    const [ openDataSourceID, setOpenDataSourceID ] = useState("");
    const [ queryItems, setQueryItems ] = useState<QueryItem[]>([]);
    const [ dataSourceListDisabled, setDataSourceListDisabled ] = useState(false);
    const [ blueprintStepExpanded, setBlueprintStepExpanded ] = useState(true);
    const [ dataSourceStepExpanded, setDataSourceStepExpanded ] = useState(false);
    const [ queryStepExpanded, setQueryStepExpanded ] = useState(false);
    const [ selectType, setSelectType ] = useState("address_zip");
    const [ phoneScores, setPhoneScores ] = useState<string[]>(["1","2","3","4","5"]);
    const [ phoneType, setPhoneType ] = useState<string>("wireless_priority");
    const [ includeDNC, setIncludeDNC ] = useState(true);
    const [ requireEmails, setRequireEmails ] = useState(false);
    const [ requirePhones, setRequirePhones ] = useState(true);
    const [ blueprintCountRequest, setBlueprintCountRequest ] = useState<QueryBlueprint>();
    const [ outputFields, setOutputFields ] = useState<string[]>([]);
    const [ buildQueryWarningMsg, setBuildQueryWarningMsg ] = useState("");
    const [ blueprintName, setBlueprintName ] = useState("");
    const [ countsBlueprint, setCountsBlueprint ] = useState<QueryBlueprint>();
    const [ openSnackbar, setOpenSnackbar] = useState(false)
    const [ snackbarMsg, setSnackbarMsg] = useState("")
    const [ scopeGlobal, setScopeGlobal] = useState(false)
    const [ customerID, setCustomerID] = useState("");
    const [ suppressOrders, setSuppressOrders] = useState(false);

    const { productsAll } = useProductsAll("", 1)
    const { blueprint } = useBlueprint(openBlueprintID)
    const { blueprints } = useBlueprints(1)
    const { dataSources, dataSourcesError } = useDataSources(1)
    const { dataSource, dataSourceError } = useDataSource(openDataSourceID)
    const { blueprintCount, blueprintCountLoading, blueprintCountError, blueprintSuppressionCount } = useBlueprintCounts(blueprintCountRequest, suppressOrders, customerID)
    const { user } = useAuth();
    const { autocompleteUsers} = useUsers(0);
    const theme = useTheme();

    useEffect(() => {
        let filteredProducts = productsAll.filter(product => product.audienceID != 0)
        // Remove the Absentee products from the list
        filteredProducts = filteredProducts.filter(product => !product.name.includes("Absentee"))

        // Add back in a generic Absentee product
        const absenteeProduct: Product = { id: "fake_absentee", 
            name: "Absentee", 
            audienceID: 9999999, 
            description: "Absentee", 
            price: 0, 
            detailedDescription:"", 
            productType: "list",
            listCounts: [],
            active: true,
            stripeID: "",
            createdAt: "",
            modifiedAt: "",
            groupName: "",
            infoURL: "",
            priceInfo:{price: 0, stripePriceID: ""},
            outputFields: [],
            features: {noDNC:false,phonesEmails:false},
            stripePrice: "",
            leadType: "",
        }
        filteredProducts.push(absenteeProduct)
        filteredProducts.sort((a, b) => a.name.localeCompare(b.name))
    }, [productsAll])

    useEffect(() => {
        if (dataSourcesError != "") {
            console.log("Data Sources Error: ", dataSourcesError)
        }
    }, [dataSourcesError])

    useEffect(() => {
        if (dataSourcesError != "") {
            console.log("Data Source Error: ", dataSourceError)
        }
    }, [dataSourceError])

    useEffect(() => {
        if (dataSource == undefined) {
            return
        }
        
        // Set default output fields
        setOutputFields(getWhitelistedOutputFields())
    }, [dataSource])

    useEffect(() => {
        if (blueprint == undefined) {
            console.log("Blueprint is undefined..bailing")
            return
        }

        if (blueprint.queryBlueprint.data_source_id != 0) {
            setDataSourceID(blueprint.queryBlueprint.data_source_id + "")
            setOpenDataSourceID(blueprint.queryBlueprint.data_source_id + "")
        }

        if(dataSource == undefined) {   
            console.log("Data Source is undefined..bailing")
            return
        }
        
        const queryItems: QueryItem[] = []
        // let foundZipField = false
        for (const queryFilter of blueprint.queryBlueprint.filters || []) {
            const newQueryItem: QueryItem = {
                id: genUniqueId(),
                filter: queryFilter,
                field: dataSource?.fields?.find(field => field.key == queryFilter.field) || { key: "", values: {}, range: false }
            }
            queryItems.push(newQueryItem)
        }
        
        setQueryItems(queryItems)
        setQueryStepExpanded(true)
        setDataSourceListDisabled(true)
        setDataSourceStepExpanded(false)
        setIncludeDNC(blueprint.queryBlueprint.dnc != undefined && blueprint.queryBlueprint.dnc == true)
        setRequireEmails(blueprint.queryBlueprint.emails === "all")
        setRequirePhones(blueprint.queryBlueprint.phones === "all")
        setSelectType(blueprint.queryBlueprint.select_type ?? "")
        setPhoneScores(blueprint.queryBlueprint.phone_score ?? [])
        setPhoneType(blueprint.queryBlueprint.phone_type ?? "")

        if (blueprint.access.scope == "user" ) {
            setBlueprintName(blueprint.name)
            setCustomerID(blueprint.access.value || "")
        } else{
            setBlueprintName("")
            setScopeGlobal(false)
        }
    }, [blueprint,dataSource])

    const handleTemplateChange = (event: SelectChangeEvent) => {
        if (event.target.value != undefined) {
            setBlueprintID(event.target.value)
        }
    };

    const handleDataSourceChange = (event: SelectChangeEvent) => {
        if (event.target.value != undefined) {
            setDataSourceID(event.target.value)
        }
    };

    const handleSelectTypeChange = (event: SelectChangeEvent) => {
        if (event.target.value != undefined) {
            setSelectType(event.target.value)
        }
    };

    const handlePhoneTypeChange = (event: SelectChangeEvent) => {
        if (event.target.value != undefined) {
            setPhoneType(event.target.value)
        }
    }

    const openDataSourceButtonClicked = () => {
        setBlueprintName("")
        setOpenDataSourceID(dataSourceID)
    }

    const openBlueprintButtonClicked = () => {
        setOpenBlueprintID(blueprintID)
    }

    const createNewButtonClicked = () => {
        setBlueprintStepExpanded(false)
        setDataSourceStepExpanded(true)
        setBlueprintID("")
        setOpenBlueprintID("")
        setDataSourceListDisabled(false)
        setQueryItems([])
    }

    const blueprintAccordionChanged = () => {
        setBlueprintStepExpanded(!blueprintStepExpanded)
    }

    const dataSourceAccordionChanged = () => {
        setDataSourceStepExpanded(!dataSourceStepExpanded)
    }

    const queryAccordionChanged = () => {
        setQueryStepExpanded(!queryStepExpanded)
    }

    const includeDNCChanged = () => {
        setIncludeDNC(!includeDNC)
    }

    const requireEmailsChanged = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setRequireEmails(checked)
    }

    const requirePhonesChanged = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setRequirePhones(checked)
    }

    const blueprintNameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setBlueprintName(event.target.value)
    }

    const scopeGlobalChanged = () => {
        setScopeGlobal(!scopeGlobal)
    }

    const suppressOrdersChanged = () => {
        setSuppressOrders(!suppressOrders)
    }

    const handlePhoneScoresChange = (event: SelectChangeEvent<typeof phoneScores>) => {
        const {
            target: { value },
          } = event;
          setPhoneScores(
            typeof value === 'string' ? value.split(',') : value,
          );
    }

    const addFieldButtonClicked = () => {
        if (dataSource == undefined) {

            return
        }

        const newQueryItem: QueryItem = {
            id: genUniqueId(),
            filter: { field: "", condition: "and", values: [], omit: false },
            field:  { key: "", values: {}, range: false }
        }
        setQueryItems([...queryItems, newQueryItem]);
    }

    const runCountsButtonClicked = () => {
        const newBlueprint = assembleBlueprint()
        if (newBlueprint == undefined) {
            return
        }

        setBlueprintCountRequest(newBlueprint)
    }

    const removeFieldFromList = (fieldID: string) => {
        const newQueryItems = queryItems.filter(queryItem => queryItem.id != fieldID)
        setQueryItems(newQueryItems)
    }

    const updateQueryFilter = (filter: QueryFilter, queryItemID: string) => {
        const newQueryItems = queryItems.map(queryItem => {
            if (queryItem.id == queryItemID) {
                queryItem.filter = filter
                queryItem.field = dataSource?.fields?.find(field => field.key == filter.field) || { key: "", values: {}, range: false }
            }
            return queryItem
        })

        setQueryItems(newQueryItems)
    }

    const assembleBlueprint = (): QueryBlueprint | undefined => {
        setBuildQueryWarningMsg("")
        const filters = assembleFilters()

        if (filters.length == 0) {
            return undefined
        }

        
        const newBlueprint: QueryBlueprint = {
            name: rootUserEmail() + "_" + genUniqueId() + "_lb",
            data_source_id: parseInt(dataSourceID),
            match_type: "",
            phones: requirePhones ? "all" : "none",
            emails: requireEmails ? "all" : "none",
            filters: assembleFilters(),
            dedupe_within: "",
            output_fields: []
        }

        if (dataSourceID != "30") {
            newBlueprint.select_type = selectType
            newBlueprint.phone_type = phoneType
            newBlueprint.phone_score = phoneScores
            newBlueprint.dnc = includeDNC
        }

        setCountsBlueprint(newBlueprint)
        return newBlueprint
    }

    const assembleFilters = (): QueryFilter[] => {
        const filters: QueryFilter[] = []
        for (const queryItem of queryItems) {
            let foundNonValue = false
            for (const value of queryItem.filter.values) {
                if (value == "") {
                    foundNonValue = true
                    break
                }
            }
            if (foundNonValue) {
                setBuildQueryWarningMsg("One or more fields have no value. Please add value(s) to all fields before running counts")
                return []
            }
            filters.push(queryItem.filter)
        }

        // if the datasource is Absentee (#30), the DNC fields need to be included filters
        if (dataSourceID == "30" && includeDNC) {
            filters.push({field: "wireless_dnc", condition: "and", values: ["Y"], omit: true})
            filters.push({field: "landline_dnc", condition: "and", values: ["Y"], omit: true})
        }

        return filters
    }

    const rootUserEmail = (): string => {
        const email = user.attributes.email
        if (email === undefined) {
            return "unknown"
        }

        const emailParts = email.split('@'); const firstPart = emailParts[0]; return firstPart;
    }

    const handleOutputFieldChange = (event: SelectChangeEvent<typeof outputFields>) => {
        const {
          target: { value },
        } = event;
        setOutputFields(
          // On autofill we get a stringified value.
          typeof value === 'string' ? value.split(',') : value,
        );
    };

    function getStyles(name: string, personName: readonly string[], theme: Theme) {
        return {
            fontWeight:
            personName.indexOf(name) === -1
                ? theme.typography.fontWeightRegular
                : theme.typography.fontWeightMedium,
        };
    }

    function getWhitelistedQueryFields(): string[] {
        if (dataSource == undefined || dataSource.fields == undefined) {
            return []
        }

        switch (dataSource.id) {
            case 13:
                return consumerQueryFieldWhiteList.sort()
            case 30:
                return absenteeQueryFieldWhiteList.sort()
            case 31:
                return absenteeQueryFieldWhiteList.sort()
        }

        return []
    }

    function getWhitelistedOutputFields(): string[] {
        if (dataSource == undefined || dataSource.fields == undefined) {
            return []
        }

        switch (dataSource.id) {
            case 13:
                return consumerOutputFieldWhiteList
            case 30:
                return absenteeOutputFieldWhiteList
            case 31:
                return absenteeOutputFieldWhiteList
        }

        return []
    }

    const saveBlueprintClicked = () => {
        if (countsBlueprint == undefined) {
            setSnackbarMsg("Cannot save Blueprint. Please run counts first.")
            setOpenSnackbar(true)
            return
        }

        if (blueprintName.trim() == "") {
            setSnackbarMsg("Please enter a name for the Blueprint")
            setOpenSnackbar(true)
            return
        }

        let bprint = blueprint
        if (bprint == undefined) {
            bprint = {
                id: "",
                name: blueprintName,
                queryBlueprint: countsBlueprint,
                createdBy: user.username || "",
                createdAt: "",
                modifiedAt: "",
                access: {
                    scope: scopeGlobal ? "global" : "user"
                }  
            }
            bprint.queryBlueprint.output_fields = outputFields
        } else {
            bprint.queryBlueprint = countsBlueprint
            bprint.createdBy = user.username || ""
            bprint.access = {
                scope: scopeGlobal ? "global" : "user"
            }  

            bprint.queryBlueprint.output_fields = outputFields

            if (bprint.name != blueprintName && blueprintID != "") {
                console.log("clearing ID")
                bprint.id = ""
            }
            bprint.name = blueprintName
        }


        if (bprint.access.scope == "user") {
            bprint.access.value = user.username
        }

        const apiURL = APIEndpoint(EndpointType.Blueprints)
        httpPost(apiURL, JSON.stringify(bprint))
        .then((data) => {
            const response = data as BlueprintResponse;
            
            if (response.status == 'error') {
                console.log("Error saving blueprint: " + response.errorMessage)
                setSnackbarMsg("Failed to save Blueprint..." + response.errorMessage || "Unknown error")
                setOpenSnackbar(true)
            } else {
              setSnackbarMsg("Blueprint saved successfully!")
              setOpenSnackbar(true)
            }
        })
        .catch((error) => { 
          setSnackbarMsg(error.message)
          setOpenSnackbar(true)
        });
    }

    const customerIDChanged = (event: React.SyntheticEvent<Element, Event>, value: AutocompleteUser | null) => {
        if (value == null) {
          setCustomerID("")
        } else {
          setCustomerID(value.id)
        }
    }

    const getFriendlyAccessDescription = (blueprint: Blueprint): string => {
        if (blueprint.access.scope == "global") {
            return "Global"
        } else if (blueprint.access.scope == "storefront") {
            if (blueprint.access.value == undefined) {
                return "Storefront"
            }
            return blueprint.access.value
        } else {
            return "User"
        }
    }

    const handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
    
        setOpenSnackbar(false);
      };

      const snackbarAction = (
        <React.Fragment>
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleSnackbarClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </React.Fragment>
      );

    const consumerQueryFieldWhiteList: string[] = ["dwelling","home_purchase_date","homeowner","est_age","home_value","lor","year_built","presence_of_children","marital_status","loan_to_value","est_income","property_type","zip"];
    const consumerOutputFieldWhiteList: string[] = ["first", "middle", "last", "address", "city", "state", "zip", "zip4", "phone", "phone_type", "dnc", "phone_2", "phone_type_2", "dnc_2", "email", "latitude", "longitude","homeowner", "loan_to_value","home_purchase_date","lor","property_type","dwelling","home_value", "year_built", "most_recent_mortgage_date", "most_recent_mortgage_amount","exact_age","est_age","est_income","marital_status","presence_of_children", "education"];
    const absenteeQueryFieldWhiteList: string[] = ["property_zip"]
    const absenteeOutputFieldWhiteList: string[] = ["first", "middle", "last", "owner_address", "owner_city", "owner_state", "owner_zip", "owner_zip4", "wireless", "landline", "email", "land_use", "property_full_address", "property_city", "property_state", "property_zip", "wireless_dnc", "landline_dnc", "latitude", "longitude","sale_price", "sale_date", "level", "bedrooms", "total_baths", "living_square_feet", "year_built", "loan_to_value", "most_recent_mortgage_date", "most_recent_mortgage_amount"];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                <Title>List Builder</Title>
                <Grid sx={{}}>
                    <Accordion expanded={blueprintStepExpanded} onChange={blueprintAccordionChanged}>
                        <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        >
                        <Typography sx={{fontWeight:"bold"}}>1: Start from an existing Blueprint or create a new one from scratch</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FormControl fullWidth size="small" sx={{width:300}}>
                                <InputLabel id="trans-type-label">Blueprint</InputLabel>
                                <Select labelId="transType" label="Blueprint" onChange={handleTemplateChange} size="small" value={blueprintID}>
                                    {blueprints && blueprints.map((bp) => (
                                        <MenuItem value={bp.id} key={bp.id}>{bp.name} [{getFriendlyAccessDescription(bp)}]</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="contained" color="primary" sx={{ ml: 2 }} onClick={openBlueprintButtonClicked}>Open</Button>
                            <Button variant="contained" color="primary" sx={{ ml: 2 }} onClick={createNewButtonClicked}>Create New</Button>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion expanded={dataSourceStepExpanded} onChange={dataSourceAccordionChanged}>
                        <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        >
                        <Typography sx={{fontWeight:"bold"}}>2: Select a data source</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FormControl fullWidth size="small" sx={{width:300}}>
                                <InputLabel id="trans-type-label">Data Source</InputLabel>
                                <Select labelId="transType" disabled={dataSourceListDisabled} label="Data Source" onChange={handleDataSourceChange} size="small" value={dataSourceID}>
                                    {dataSources && dataSources.map((dataSource) => (
                                        <MenuItem key={dataSource.id} value={dataSource.id}>{dataSource.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="contained" disabled={dataSourceListDisabled} color="primary" sx={{ ml: 2 }} onClick={openDataSourceButtonClicked}>Open</Button>
                            <Typography visibility={dataSourceListDisabled ? "visible" : "hidden"} sx={{color:"red"}}>Since a blueprint was selected, the Data Source cannot be changed</Typography>
                            {dataSourceError != "" &&
                                <Alert severity="error">{dataSourceError.toString()}</Alert>
                            }
                        </AccordionDetails>
                    </Accordion>
                    <Accordion expanded={queryStepExpanded} onChange={queryAccordionChanged}>
                        <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        >
                        <Typography sx={{fontWeight:"bold"}}>3: Build Query</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {dataSource != undefined
                            ? <Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth size="small" sx={{}}>
                                            <InputLabel id="select-type-label">Select Type</InputLabel>
                                            <Select labelId="selectType" label="Select Type" onChange={handleSelectTypeChange} size="small" value={selectType} MenuProps={{sx: {"&& .Mui-selected": {backgroundColor: props.storefront.activeColors.leftMenu.icon}}}}>
                                                { dataSource && dataSource.dedupes && dataSource.dedupes.map((dedupe) => (
                                                    <MenuItem value={dedupe.field} key={dedupe.field}>{dedupe.name} </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth size="small" sx={{}}>
                                            <InputLabel id="phones-scores-label">Phone Scores</InputLabel>
                                            <Select sx={{}} multiple labelId="phoneScores" label="Phone Scores" 
                                                onChange={handlePhoneScoresChange} MenuProps={{sx: {"&& .Mui-selected": {backgroundColor: props.storefront.activeColors.leftMenu.icon}}}} size="small" value={phoneScores}>
                                                <MenuItem value="1" key="1">1</MenuItem>
                                                <MenuItem value="2" key="2">2</MenuItem>
                                                <MenuItem value="3" key="3">3</MenuItem>
                                                <MenuItem value="4" key="4">4</MenuItem>
                                                <MenuItem value="5" key="5">5</MenuItem>
                                                <MenuItem value="6" key="6">6</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth size="small" sx={{}}>
                                            <InputLabel id="phones-label">Phones</InputLabel>
                                            <Select sx={{}} labelId="phones" label="Phones" 
                                                onChange={handlePhoneTypeChange}
                                                MenuProps={{sx: {"&& .Mui-selected": {backgroundColor: props.storefront.activeColors.leftMenu.icon}}}} size="small" value={phoneType}>
                                                <MenuItem value="landline" key="landline">Landlines</MenuItem>
                                                <MenuItem value="wireless" key="wireless">Mobile</MenuItem>
                                                <MenuItem value="wireless_priority" key="wireless_priority">Mobile (priority) + Landlines</MenuItem>
                                                <MenuItem value="landline_priority" key="landline_priority">Landline (priority) + Mobile</MenuItem>
                                                <MenuItem value="none" key="none">None</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControlLabel control={<Switch defaultChecked />} label="Include DNC" value={includeDNC} onChange={includeDNCChanged} />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControlLabel control={<Switch onChange={requireEmailsChanged} checked={requireEmails}/>} label="Require Emails" />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControlLabel control={<Switch checked={requirePhones} onChange={requirePhonesChanged}/>} label="Require Phones" />
                                    </Grid>
                                </Grid>
                                <List>
                                    {queryItems.map((queryItem) => (
                                        <ListItem key={queryItem.id} sx={{mt:2}}>
                                            <QueryFieldForm key={queryItem.id} queryItem={queryItem} dataSource={dataSource} storefront={props.storefront} removeField={removeFieldFromList} updateFilter={updateQueryFilter} whitelistedQueryFields={getWhitelistedQueryFields()} />
                                        </ListItem>
                                    ))}
                                </List>
                                <Button variant="contained" color="primary" sx={{ ml: 0 }} onClick={addFieldButtonClicked}>Add Field</Button>
                            </Grid>
                            : <Alert severity="warning">No Data Source Selected</Alert>
                            }
                            {buildQueryWarningMsg != "" && <Alert sx={{mt:1}} severity="warning">{buildQueryWarningMsg}</Alert>}
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        >
                        <Typography sx={{fontWeight:"bold"}}>4: Run Counts</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {queryItems.length > 0
                            ?<Grid>
                                <Box sx={{mb:2}}>
                                    <FormControlLabel control={<Switch checked={suppressOrders} onChange={suppressOrdersChanged}/>} label="Suppress previous customer orders" />
                                    {suppressOrders &&
                                        <Autocomplete disablePortal isOptionEqualToValue={(option: AutocompleteUser, value: AutocompleteUser) => option.id === value.id} id="existingCustomer" onChange={customerIDChanged} options={autocompleteUsers} size="small"  sx={{ width: 360 }}
                                            renderInput={(params) => <TextField {...params} label="Customer Name" /> }/>
                                    }
                                </Box>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Button variant="contained" color="primary" sx={{ ml: 2 }} onClick={runCountsButtonClicked}>Run Counts</Button>
                                    {blueprintCountLoading
                                        ?<Typography>{blueprintCountLoading ? "Requesting counts..." : ""}</Typography>
                                        : blueprintCountError != "" 
                                        ? <Alert severity="error">{blueprintCountError}</Alert>
                                        : <Stack direction="row">
                                            <Typography>Count Result: {blueprintCount}</Typography>
                                            { suppressOrders && <Typography sx={{ml:1}}>- Previous orders suppressed count: {blueprintSuppressionCount}</Typography> }
                                        </Stack>
                                    }
                                </Stack>
                            </Grid>
                            : <Alert severity="warning">Please add fields to the query before running counts</Alert>
                            }
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        >
                        <Typography sx={{fontWeight:"bold"}}>5: Select Output Fields</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {blueprintCount > 0
                                ?<FormControl sx={{ width: 590 }}>
                                    <InputLabel id="demo-multiple-chip-label">Output Fields</InputLabel>
                                    <Select
                                        labelId="output-fields-label"
                                        id="output-fields-chip"
                                        multiple
                                        value={outputFields}
                                        onChange={handleOutputFieldChange}
                                        input={<OutlinedInput id="select-multiple-output-field" label="Output Fields" />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} />
                                            ))}
                                            </Box>
                                        )}
                                        MenuProps={MenuProps}
                                    >
                                    {getWhitelistedOutputFields().map((field) => (
                                        <MenuItem
                                            key={field}
                                            value={field}
                                            style={getStyles(field, outputFields, theme)}
                                            >
                                            {field}
                                        </MenuItem>
                                    ))}
                                    </Select>
                                </FormControl>
                            : <Alert severity="warning">The query must yield at least one record before continuing</Alert>
                            }
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        >
                        <Typography sx={{fontWeight:"bold"}}>6: Save the Blueprint</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {outputFields.length > 0
                               ? <Grid>
                                    <Typography>Once the Blueprint is saved, it can be ordered from the "Place New Order" tab. The blueprint name will appear in the order invoice so name it appropriately (i.e., "Distressed leads - Wilma Flintstone").</Typography>
                                    <TextField sx={{mt:1}} fullWidth size="small" label="Blueprint Name" variant="outlined" value={blueprintName} onChange={blueprintNameChanged} />
                                    <FormControlLabel control={<Switch checked={scopeGlobal} onChange={scopeGlobalChanged}/>} label="Make available to all users" />
                                    <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={saveBlueprintClicked}>Save Blueprint</Button>
                                </Grid>
                            : <Alert severity="warning">Cannot save Blueprint until output fields are specified</Alert>
                            }
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Paper>
          </Grid>
        </Grid>
        <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            message={snackbarMsg}
            action={snackbarAction}
        />
      </Container>
    );
};

export default ListBuilder;